<template>
    <div>
        <b-modal
            :visible="visible"
            id="admin-user-modal"
            size="lg"
            :ok-title="`${selected_user ? 'Edit' : 'Add'} User`"
            :ok-disabled="!validated && !saving"
            @cancel="cancel"
            @close="cancel"
            @ok.prevent="save"
            @hidden="reset"
            no-close-on-esc
        >
            <template #modal-title>
                <span v-if="selected_user">Edit</span>
                <span v-else>Add</span>
                User
            </template>

            <b-form-group label="Email" label-for="user-modal-email">
                <b-form-input
                    id="user-modal-email"
                    type="text"
                    placeholder="Enter Email"
                    aria-label="email"
                    v-model="email"
                    required
                    :disabled="Boolean(selected_user)"
                />
                <b-form-invalid-feedback :state="validated" v-if="!saving">
                    <span v-if="emailIsDuplicated">
                        This email already exists.
                    </span>
                    <span v-else-if="!emailIsSet">
                        Email is required.
                    </span>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="First Name" label-for="user-modal-first-name">
                <b-form-input
                    id="user-modal-first-name"
                    type="text"
                    placeholder="Enter First Name"
                    aria-label="first name"
                    v-model="first_name"
                    required
                    :disabled="Boolean(selected_user)"
                />
                <b-form-invalid-feedback :state="validated" v-if="!saving">
                    <span v-if="!firstNameIsSet">
                        First name is required.
                    </span>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Last Name" label-for="user-modal-last-name">
                <b-form-input
                    id="user-modal-last-name"
                    type="text"
                    placeholder="Enter Last Name"
                    aria-label="last name"
                    v-model="last_name"
                    required
                    :disabled="Boolean(selected_user)"
                />
                <b-form-invalid-feedback :state="validated" v-if="!saving">
                    <span v-if="!lastNameIsSet">
                        Last name is required.
                    </span>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label-for="user-modal-password">
                <template v-slot:label>
                    Password
                    <b-btn
                        class="ml-2 p-0 border-0"
                        size="sm"
                        variant="link"
                        :disabled="emailIsEmployee"
                        @click="generatePassword"
                    >
                        Generate Password
                    </b-btn>
                </template>
                <b-input-group>
                    <b-form-input
                        id="user-modal-password"
                        type="text"
                        placeholder="Enter Password"
                        aria-label="password"
                        v-model="password"
                        :disabled="emailIsEmployee"
                    />
                    <b-input-group-append>
                        <CopyToClipboardButton :value="password" :as-html="false" variant="outline-secondary" />
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>

            <b-form-group label-for="user-modal-roles">
                <template #label>
                    Roles
                    <b-button id="user-modal-roles-tip" variant="link" class="p-0">
                        <font-awesome-icon :icon="['fad', 'info-circle']"/>
                    </b-button>
                    <b-tooltip target="user-modal-roles-tip" triggers="hover">
                        (Optional) Roles to assign to the user, upon creation/modification.
                    </b-tooltip>
                </template>

                <multiselect
                    id="user-modal-roles"
                    v-model="selected_roles"
                    placeholder="Select Roles"
                    label="name"
                    track-by="id"
                    :options="roles"
                    :multiple="true"
                />
            </b-form-group>

            <b-form-group label-for="user-modal-skus">
                <template #label>
                    SKUs Assigned
                    <b-button id="user-modal-skus-tip" variant="link" class="p-0">
                        <font-awesome-icon :icon="['fad', 'info-circle']"/>
                    </b-button>
                    <b-tooltip target="user-modal-skus-tip" triggers="hover">
                        (Optional) Publication SKUs to assign to the user. This will limit them to only these skus.
                    </b-tooltip>
                </template>

                <multiselect
                    id="user-modal-skus"
                    v-model="selected_skus"
                    placeholder="Select SKUs"
                    :custom-label="skuLabel"
                    track-by="sku_id"
                    :options="skus"
                    :multiple="true"
                />
            </b-form-group>

            <b-form-group>
                <template #label>
                    Enable/Disable User
                </template>
                <b-form-checkbox v-model="active" name="active-button" switch>
                    User {{ active ? 'Enabled' : 'Disabled'}}
                </b-form-checkbox>
            </b-form-group>
        </b-modal>
    </div>
</template>

<style></style>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import PasswordGen from 'passwordgen'
import copy from 'copy-html-to-clipboard'
import CopyToClipboardButton from '@/components/CopyToClipboardButton'

const passwordGenerator = new PasswordGen()

export default {
    data: () => ({
        saving: false,
        email: '',
        first_name: '',
        last_name: '',
        selected_roles: null,
        selected_skus: null,
        password: '',
        active: true,
    }),
    props: {
        visible: Boolean,
        selected_user: Object,
    },
    components: {CopyToClipboardButton},
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user,
            users: state => state.admin.users,
            roles: state => state.admin.roles,
            skus: state => state.admin.skus,
        }),
        validated() {
            return (
                this.firstNameIsSet &&
                this.lastNameIsSet &&
                this.emailIsSet &&
                this.emailIsDuplicated === false
            )
        },
        firstNameIsSet() {
            return this.first_name.length !== 0
        },
        lastNameIsSet() {
            return this.last_name.length !== 0
        },
        emailIsSet() {
            return this.email.length !== 0
        },
        emailIsDuplicated() {
            const emailAlreadyExists = this.emails.includes(this.email)
            if (this.selected_user) {
                return this.selected_user.email !== this.email && emailAlreadyExists
            }

            return emailAlreadyExists
        },
        emails() {
            return this.users.map(user => user.email)
        },
        emailIsEmployee() {
            return this.emailIsSet && this.email.toLowerCase().includes('@studiesweekly.com')
        },
    },
    methods: {
        copy,
        cancel() {
            this.$emit('hide')
        },
        async save() {
            try {
                this.saving = true
                const userData = {
                    email: this.email,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    roles: this.selected_roles,
                    skus: this.selected_skus,
                    active: this.active,
                }

                if (!this.emailIsEmployee && this.password.length > 0) {
                    userData.password = this.password
                }

                if (!this.selected_user) {
                    await this.$store.dispatch('createAdminUser', userData);
                } else {
                    await this.$store.dispatch('updateAdminUser', {
                        ...userData,
                        id: this.selected_user.id,
                    })
                }

                // reload the stores that would be updated
                await this.$store.dispatch('getAdminUsers')
                await this.$store.dispatch('getAdminRoles')

                this.$emit('hide')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to save user: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        reset() {
            this.saving = false
            this.password = ''

            if (!this.selected_user) {
                this.email = ''
                this.first_name = ''
                this.last_name = ''
                this.selected_roles = null
                this.selected_skus = null
                this.active = true
            } else {
                this.email = this.selected_user.email
                this.first_name = this.selected_user.first_name
                this.last_name = this.selected_user.last_name
                this.selected_roles = this.selected_user.roles
                this.selected_skus = this.selected_user.skus
                this.active = Boolean(this.selected_user.active)
            }
        },
        skuLabel(option) {
            return `${option.sku} (${option.title})`;
        },
        generatePassword() {
            this.password = passwordGenerator.phrase(4, {separator: '-', symbols: false})
        },
    },
    mounted() {
        this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
            if (modalId !== 'admin-user-modal') {
                return
            }

            this.reset()
        })
    }
}
</script>
